import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

interface PhaseIndicatorProps {
  currentPhase: 0 | 1 | 2 | 3 | 4;
  onPhaseClick?: (phase: 0 | 1 | 2 | 3 | 4) => void;
}

const PHASES = [
  { label: 'Intake' },
  { label: 'Plan' },
  { label: 'Slides' },
  { label: 'Images' },
  { label: 'Export' },
];

export const PhaseIndicator: React.FC<PhaseIndicatorProps> = ({
  currentPhase,
  onPhaseClick,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    {PHASES.map((phase, i) => {
      const isCompleted = i < currentPhase;
      const isCurrent  = i === currentPhase;
      const isClickable = isCompleted && Boolean(onPhaseClick);
      const phaseNum = i as 0 | 1 | 2 | 3 | 4;

      return (
        <React.Fragment key={i}>
          {i > 0 && (
            <Box
              sx={{
                width: 18, height: 1, mx: 0.25, flexShrink: 0,
                bgcolor: isCompleted ? 'primary.main' : 'divider',
                transition: 'background-color 0.2s',
              }}
            />
          )}
          <Tooltip
            title={
              isClickable
                ? `Back to ${phase.label}`
                : isCurrent
                ? `Current: ${phase.label}`
                : phase.label
            }
            placement="bottom"
            arrow
          >
            <Box
              onClick={() => isClickable && onPhaseClick?.(phaseNum)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 0.875, py: 0.4,
                borderRadius: 10,
                cursor: isClickable ? 'pointer' : 'default',
                bgcolor: isCurrent ? 'primary.main' : 'transparent',
                border: '1px solid',
                borderColor: isCurrent
                  ? 'primary.main'
                  : isCompleted
                  ? 'primary.light'
                  : 'transparent',
                transition: 'all 0.15s ease',
                '&:hover': isClickable
                  ? { bgcolor: 'primary.50', borderColor: 'primary.light' }
                  : {},
              }}
            >
              {/* Number / check bubble */}
              <Box
                sx={{
                  width: 17, height: 17, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: isCurrent
                    ? 'white'
                    : isCompleted
                    ? 'primary.main'
                    : 'action.disabledBackground',
                  transition: 'background-color 0.15s',
                }}
              >
                {isCompleted ? (
                  <CheckIcon sx={{ fontSize: 10, color: 'white' }} />
                ) : (
                  <Typography
                    sx={{
                      fontSize: '0.58rem', fontWeight: 800, lineHeight: 1,
                      color: isCurrent ? 'primary.main' : 'text.disabled',
                    }}
                  >
                    {i}
                  </Typography>
                )}
              </Box>

              {/* Phase label */}
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: isCurrent ? 700 : 500,
                  color: isCurrent
                    ? 'white'
                    : isCompleted
                    ? 'primary.main'
                    : 'text.disabled',
                  userSelect: 'none',
                  lineHeight: 1,
                }}
              >
                {phase.label}
              </Typography>
            </Box>
          </Tooltip>
        </React.Fragment>
      );
    })}
  </Box>
);
